// CPI-1283: a booking may carry up to four related purchasers, not one. The cap and the rules
// for rewriting an opportunity's customer_ids live here rather than in the 1,400-line booking
// page because this repo has no React testing library, so a pure module is the only layer that
// can be tested.
//
// This module makes no country assumption. Gating is the caller's job: pass the limit in.
// The booking-detail screen is NOT itself behind a country flag — its main OPPORTUNITY.READ
// runs everywhere, and only the navigation into it is gated (on TokenPayment, Vietnam only),
// so a direct URL reaches that screen from any tenant. See
// docs/merge-audit-main-svn-to-master.md on gating.

// Equal to MAX_RELATED_PURCHASERS in the buyer site's
// mhub-marketplace-web/src/utils/relatedPurchasers.ts (CPI-1027, the same AC).
export const MAX_RELATED_PURCHASERS = 4;

// The rule before CPI-1283, and the default when a caller names no limit. Defaulting to the
// old behaviour means a caller that forgets to gate degrades to one, never silently to four.
export const LEGACY_RELATED_PURCHASER_LIMIT = 1;

// How many a booking may hold, given whether this tenant has the Vietnam booking concept.
export const relatedPurchaserLimit = (isBookingConcept) => (
  isBookingConcept ? MAX_RELATED_PURCHASERS : LEGACY_RELATED_PURCHASER_LIMIT
);

// The Add control is offered only in a status that allows managing purchasers and below the
// caller's limit.
export const canAddRelatedPurchaser = ({
  count, canModify, max = LEGACY_RELATED_PURCHASER_LIMIT,
}) => Boolean(canModify) && Number(count || 0) < Number(max);

// Order-preserving union of id lists. Falsy ids are dropped and the first occurrence wins.
export const mergeRelatedCustomerIds = (...lists) => {
  const merged = [];
  lists.forEach((list) => (list || []).forEach((id) => {
    if (id && !merged.includes(id)) merged.push(id);
  }));
  return merged;
};

// Merge, never rebuild: whatever the opportunity has stored is carried through untouched, so an
// id this screen does not model — the primary customer's, when it is stored in customer_ids —
// is never dropped by an add. A repeated id is a no-op.
export const appendRelatedCustomerId = (existing, customerId) => (
  mergeRelatedCustomerIds(existing, customerId ? [customerId] : [])
);

// Drops one customer, leaving every other id in place.
export const removeRelatedCustomerId = (existing, customerId) => (
  (existing || []).filter((id) => id && id !== customerId)
);
